import CustomSelect from 'components/CustomSelect';
import Icon from 'components/Icon';
import RankingItem from 'components/RankingItem';
import { queryKeys, routes, TEXT_STRING } from '../../constants';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ERankingPeriod, ERankingProjectType } from 'types';
import { useQuery } from '@tanstack/react-query';
import { getFemaleRanking } from 'api';
import { useAppStore } from 'store';
import './ranking.css';

const periods = [
  {
    title: TEXT_STRING.RANKING.TIME_PERIOD.YESTERDAY,
    id: ERankingPeriod.YESTERDAY,
  },
  {
    title: TEXT_STRING.RANKING.TIME_PERIOD.LAST_WEEK,
    id: ERankingPeriod.LAST_WEEK,
  },
  {
    title: TEXT_STRING.RANKING.TIME_PERIOD.LAST_MONTH,
    id: ERankingPeriod.LAST_MONTH,
  },
];

const projectTypes = [
  { title: '個テコ', id: ERankingProjectType.INDIVIDUAL },
  { title: '新人', id: ERankingProjectType.NEWCOMER },
  { title: '団体', id: ERankingProjectType.GROUP },
];

// Radix Select は空文字を値にできないので、全国はこの値で持ち、API には渡さない
const ALL_AREAS = 'all';

export default function Ranking() {
  const navigate = useNavigate();
  const { franchiseAreas } = useAppStore();
  const [period, setPeriod] = useState(ERankingPeriod.YESTERDAY);
  const [projectType, setProjectType] = useState(
    ERankingProjectType.INDIVIDUAL,
  );
  const [area, setArea] = useState(ALL_AREAS);
  const areaId = area === ALL_AREAS ? undefined : Number(area);
  const ranking = useQuery({
    queryKey: [queryKeys.RANKING, projectType, period, area],
    queryFn: () => getFemaleRanking({ projectType, period, areaId }),
  });
  const areaOptions = useMemo(
    () => [
      { value: ALL_AREAS, name: '全国' },
      ...(franchiseAreas ?? [])
        .filter((item) => !item.isOtherArea)
        .map((item) => ({ value: String(item.id), name: item.name })),
    ],
    [franchiseAreas],
  );
  const selectedType = projectTypes.find(
    (item) => item.id === projectType,
  )?.title;
  const selectedPeriod = periods.find((item) => item.id === period)?.title;
  const selectedArea =
    area === ALL_AREAS
      ? undefined
      : areaOptions.find((item) => item.value === area)?.name;

  return (
    <div className="ranking-page h-full overflow-y-auto">
      <section className="ranking-hero" aria-label="ランキングの絞り込み">
        <button
          type="button"
          className="ranking-hero__back"
          aria-label="戻る"
          onClick={() => navigate(routes.SEARCH)}
        >
          <Icon icon="chevronLeft" color="currentColor" />
        </button>
        <p className="ranking-hero__eyebrow">TECO GIRLS RANKING</p>
        <div className="ranking-hero__heading">
          <h1 className="ranking-hero__title">
            {TEXT_STRING.RANKING.OVERALL_RATING}
          </h1>
          <span className="ranking-hero__selection">
            {selectedType} ／ {selectedPeriod}
            {selectedArea && ` ／ ${selectedArea}`}
          </span>
        </div>
        <div
          className="ranking-type-tabs"
          role="tablist"
          aria-label="ランキング種別"
        >
          {projectTypes.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={projectType === item.id}
              className="ranking-type-tabs__button"
              onClick={() => setProjectType(item.id)}
            >
              {item.title}
            </button>
          ))}
        </div>
        <div className="ranking-period-row">
          <div
            className="ranking-period-tabs"
            role="tablist"
            aria-label="集計期間"
          >
            {periods.map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={period === item.id}
                className="ranking-period-tabs__button"
                onClick={() => setPeriod(item.id)}
              >
                {item.title}
              </button>
            ))}
          </div>
          <CustomSelect
            name="area"
            ariaLabel="エリア"
            value={area}
            options={areaOptions}
            onChange={setArea}
            align="end"
            sideOffset={6}
            classes={{
              trigger: `ranking-area${selectedArea ? ' ranking-area--active' : ''}`,
              value: 'ranking-area__value',
              content: 'ranking-area__menu',
              viewport: 'ranking-area__list',
              item: 'ranking-area__option',
            }}
            renderValue={(option) => (
              <>
                <Icon
                  icon="location"
                  width={12}
                  height={12}
                  color="currentColor"
                />
                <span className="ranking-area__name">{option?.name}</span>
              </>
            )}
            renderOption={(option) => (
              <>
                {option.name}
                {option.value === area && (
                  <Icon icon="check" color="currentColor" />
                )}
              </>
            )}
            indicator={
              <span className="ranking-area__chevron" aria-hidden>
                <Icon icon="chevronRight" color="currentColor" />
              </span>
            }
          />
        </div>
      </section>
      <section className="ranking-results" aria-label="ランキング一覧">
        {ranking.isLoading || ranking.isRefetching ? (
          Array.from({ length: 3 }, (_, index) => (
            <div
              className="ranking-results__skeleton animate-pulse"
              key={index}
            />
          ))
        ) : ranking.data?.length ? (
          ranking.data.map((row) => <RankingItem key={row.user.id} {...row} />)
        ) : (
          <p className="ranking-results__empty">{TEXT_STRING.COMMON.NO_DATA}</p>
        )}
      </section>
    </div>
  );
}
